import React from 'react';
import type { ReactNodeCompat } from '@/types/react-compat';
import 'katex/dist/katex.min.css';
import { logError } from '@/lib/logger';

interface MathRendererProps {
  content: string;
  display?: boolean;
}

// Cache per evitare re-renderizzazioni
const mathCache = new Map<string, ReactNodeCompat>();

export const MathRenderer = React.memo(({
  content,
  display = false,
}: MathRendererProps) => {
  const [error, setError] = React.useState<string | null>(null);
  const [rendered, setRendered] = React.useState<ReactNodeCompat | null>(null);
  const cacheKey = `${content}-${display}`;

  React.useEffect(() => {
    // Controlla se abbiamo già renderizzato questo contenuto
    if (mathCache.has(cacheKey)) {
      setRendered(mathCache.get(cacheKey));
      setError(null);
      return;
    }

    const renderMath = async () => {
      try {
        // Import dinamico per evitare problemi di SSR
        const katex = await import('katex');

        const html = katex.renderToString(content, {
          displayMode: display,
          throwOnError: false,
          errorColor: '#cc0000',
        });

        const renderedElement = (
          <span
            dangerouslySetInnerHTML={{ __html: html }}
            className={display ? 'block my-4' : 'inline'}
          />
        );

        // Salva nel cache
        mathCache.set(cacheKey, renderedElement);
        setRendered(renderedElement);
        setError(null);
      } catch (err) {
        logError('Errore nel rendering della matematica:', err);
        setError(`Errore nel rendering: ${content}`);
        setRendered(null);
      }
    };

    if (content.trim()) {
      renderMath();
    }
  }, [content, display, cacheKey]);

  if (error) {
    return (
      <span className="text-red-500 text-sm font-mono bg-red-50 dark:bg-red-950/20 px-2 py-1 rounded">
        {error}
      </span>
    );
  }

  if (!rendered) {
    return (
      <span className="text-muted-foreground animate-pulse">
        Rendering matematica...
      </span>
    );
  }

  return <>{rendered}</>;
});

// Funzione helper per estrarre blocchi di matematica dal testo
export function extractMathBlocks(
  text: string
): Array<{ type: 'text' | 'math'; content: string; display: boolean }> {
  const blocks: Array<{
    type: 'text' | 'math';
    content: string;
    display: boolean;
  }> = [];

  // Regex per blocchi di matematica display ($$...$$)
  const displayMathRegex = /\$\$([\s\S]*?)\$\$/g;
  // Regex per matematica inline ($...$)
  const inlineMathRegex = /\$([^$\n]+?)\$/g;

  let lastIndex = 0;
  let match;

  // Trova tutti i blocchi display math
  while ((match = displayMathRegex.exec(text)) !== null) {
    // Aggiungi testo prima del blocco
    if (match.index > lastIndex) {
      blocks.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
        display: false,
      });
    }

    // Aggiungi il blocco di matematica
    blocks.push({
      type: 'math',
      content: match[1].trim(),
      display: true,
    });

    lastIndex = match.index + match[0].length;
  }

  // Processa il testo rimanente per matematica inline
  const remainingText = text.slice(lastIndex);
  let inlineLastIndex = 0;

  while ((match = inlineMathRegex.exec(remainingText)) !== null) {
    // Aggiungi testo prima del blocco inline
    if (match.index > inlineLastIndex) {
      blocks.push({
        type: 'text',
        content: remainingText.slice(inlineLastIndex, match.index),
        display: false,
      });
    }

    // Aggiungi il blocco di matematica inline
    blocks.push({
      type: 'math',
      content: match[1].trim(),
      display: false,
    });

    inlineLastIndex = match.index + match[0].length;
  }

  // Aggiungi il testo rimanente
  if (inlineLastIndex < remainingText.length) {
    blocks.push({
      type: 'text',
      content: remainingText.slice(inlineLastIndex),
      display: false,
    });
  }

  return blocks;
}
